import { sumBy } from "lodash";
import { initialClaim, InitialTimeSheetDataFn } from "./InitialTimeSheetData";
import { DRAFT, MAX_WORKING_HOURS, MIN_WORKING_HOURS, PENDING } from ".";
import { notification } from "antd";
import { useCallback, useEffect, useState } from "react";
import { setCacheTimesheet } from "redux/actions";
import { useDispatch, useSelector } from "react-redux";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import {
  getClientid,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import moment from "moment";
import { CALL_API } from "common/API";
import { SUB_CLIENT_ID_1 } from "common/Constants";

const EXTENDED_MAX_WORKING_HOURS = 24;

const useTimesheetfns = ({
  personIncharge,
  OfficeSuperVisorUserId,
  currentWeek,
  form,
  setPersonInCharge,
  week,
  timesheetDataRes,
  onSubmit,
  isFetched,
  duplicateTimesheet,
}) => {
  const dispatch = useDispatch();

  const clientId = useSelector(getClientid);
  const userRoleId = useSelector(getUserRoleId);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const monday = moment(currentWeek, "YYYY-MM-DD");
  const sunday = moment(currentWeek, "YYYY-MM-DD").add(6, "days");

  const { key: holidayKey, url: holdiayUrl } =
    queryKeys.getHolidayList(clientId);

  const { data: holidayDataRes } = useFetch(
    [holidayKey, monday, sunday],
    holdiayUrl,
    {
      enabled: !!clientId,
    },
    {
      start_date: monday,
      end_date: sunday,
    }
  );

  const holidays = holidayDataRes?.response;

  const hasHolidays = holidays?.find((holiday) =>
    holiday?.location_id?.includes(currentUser?.baselocationid)
  );

  const initialTimeSheet = InitialTimeSheetDataFn(currentWeek, hasHolidays);

  const [timeSheet, setTimeSheet] = useState(initialTimeSheet);

  // Whether the current week now qualifies for an "AP" (Approved Absence) row -
  // a public holiday, or the user's own leave having since been approved.
  // Re-checked against live data rather than trusting a stored value, since
  // leave approval status can change after a timesheet document was created.
  const checkApEligibility = async () => {
    const [holidaysRes, leaveRes] = await Promise.all([
      CALL_API(`holiday-list/${clientId}`, "post", {
        start_date: monday,
        end_date: sunday,
      }),
      CALL_API(`leave-list/${clientId}`, "post", {
        status_id: "",
        officesupervisorid: [""],
        selected_role_id: userRoleId,
        start_date: monday,
        end_date: sunday,
      }),
    ]);

    const myLeaves = leaveRes.leaves.filter(
      (leave) =>
        leave.userid === userInfo.userid &&
        leave?.status_id === "5" &&
        leave?.having_cancel_request === "0"
    );

    const hasTakenLeave = myLeaves.length > 0;

    const hasHolidays = holidaysRes?.find((holiday) =>
      holiday?.location_id?.includes(currentUser?.baselocationid)
    );

    return Boolean(hasHolidays || hasTakenLeave);
  };

  const setInitialTimesheet = async () => {
    // setTimeSheet(initialTimeSheet);
    // form.setFieldsValue(initialTimeSheet);

    const isApEligible = await checkApEligibility();

    const initialTimeSheetWithHolidays = InitialTimeSheetDataFn(
      currentWeek,
      isApEligible
    );

    setTimeSheet(initialTimeSheetWithHolidays);
    form.setFieldsValue(initialTimeSheetWithHolidays);
  };

  // A previously-saved document (e.g. reopened from Declined to fix and
  // resubmit) is loaded as-is and never re-runs the AP check above, so a row
  // approved for leave/holiday *after* the document was first created never
  // gets its AP row. Backfill it here, additively - never touching or
  // replacing rows the user already filled in.
  const backfillApRow = async (existingRows) => {
    const alreadyHasApRow = existingRows?.some((row) => row?.client === "ap");
    if (alreadyHasApRow || !(await checkApEligibility())) {
      return existingRows;
    }
    const [apRow] = InitialTimeSheetDataFn(currentWeek, true);
    return [...existingRows, apRow];
  };

  useEffect(() => {
    if (!isFetched) {
      return;
    }
    if (timesheetDataRes?.response?.response?.timeSheet) {
      const existingRows = timesheetDataRes?.response?.response?.timeSheet;
      backfillApRow(existingRows).then((rows) => {
        setTimeSheet(rows);
        form.setFieldsValue(rows);
      });
    } else if (duplicateTimesheet) {
      setTimeSheet(duplicateTimesheet);
      form.setFieldsValue(duplicateTimesheet);
    } else {
      setInitialTimesheet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetDataRes, form]);

  const generateTimeSheetData = (
    values,
    draft = false,
    originalTimeSheet = [],
    isSumbitDraftIsPending = false,
    isDeclinedSave = false,
    isAddedNewRow = false,
    isDeclineSubmit = false
  ) => {
    return Object.values(values).map((value, index) => {
      const personInCharge =
        personIncharge?.[index] || OfficeSuperVisorUserId || "";
      const isClientAP = value?.client === "ap";
      const original = originalTimeSheet?.[index];

      const hasChanged =
        original &&
        (original.mon !== parseFloat(value?.mon) ||
          original.tue !== parseFloat(value?.tue) ||
          original.wed !== parseFloat(value?.wed) ||
          original.thu !== parseFloat(value?.thu) ||
          original.fri !== parseFloat(value?.fri) ||
          original.sat !== parseFloat(value?.sat) ||
          original.sun !== parseFloat(value?.sun) ||
          original.remarks !== value?.remarks);
      const wasDeclined = original?.status === "declined";

      const status = draft
        ? DRAFT
        : wasDeclined && isDeclinedSave
        ? DRAFT
        : wasDeclined && isDeclineSubmit
        ? PENDING
        : original?.status === DRAFT ||
          (value?.status === "draft" && isAddedNewRow)
        ? DRAFT
        : original?.status === DRAFT ||
          (isSumbitDraftIsPending && value?.status === DRAFT)
        ? PENDING
        : value?.status || original?.status || PENDING;

      return {
        ...value,
        mon: parseFloat(value?.mon) || 0,
        tue: parseFloat(value?.tue) || 0,
        wed: parseFloat(value?.wed) || 0,
        thu: parseFloat(value?.thu) || 0,
        fri: parseFloat(value?.fri) || 0,
        sat: isClientAP ? 0 : parseFloat(value?.sat) || 0,
        sun: isClientAP ? 0 : parseFloat(value?.sun) || 0,
        sum: parseFloat(value?.sum) || 0,
        actual_to_date:
          value?.engagement === "nc" || value?.engagement === "ap"
            ? 0
            : parseFloat(value?.actual_to_date) || 0,
        claims: value?.claims || [],
        personInCharge,
        currentWeek: currentWeek.format("YYYY-MM-DD"),
        ncClient: value?.ncClient || null,
        subEngagement: value?.subEngagement || null,
        remarks: value?.remarks || "",
        status: isClientAP ? "approved" : status,
        approval: original?.approval || {},
        declined: wasDeclined && hasChanged ? {} : original?.declined || {},
      };
    });
  };

  const isChargeAbleAmtValid = (timeSheet) => {
    let engagementValidation = timeSheet?.some(
      (item) => item?.engagement == undefined || item?.engagement == null
    );
    let clientNameValidation = timeSheet?.some((item) => item?.client == null);

    const daysOfWeek = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const dayNames = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const maxWorkingHours =
      clientId === SUB_CLIENT_ID_1 ? EXTENDED_MAX_WORKING_HOURS : MAX_WORKING_HOURS;

    const validateDayTotal = (total, dayName, isWeekend) => {
      if (clientNameValidation) {
        notification.error({
          message: `Client Field is Required`,
        });
        return false;
      }

      if (engagementValidation) {
        notification.error({
          message: `Engagement Field is Required`,
        });
        return false;
      }

      // Weekdays must be between MIN_WORKING_HOURS and maxWorkingHours
      if (
        !isWeekend &&
        (total < MIN_WORKING_HOURS || total > maxWorkingHours)
      ) {
        notification.error({
          message: `${dayName} total hours should be between ${MIN_WORKING_HOURS} and ${maxWorkingHours}`,
        });
        return false;
      }

      // Weekends (Saturday & Sunday) must be between 0 and maxWorkingHours
      if (isWeekend && (total < 0 || total > maxWorkingHours)) {
        notification.error({
          message: `${dayName} total hours should be between 0 and ${maxWorkingHours}`,
        });
        return false;
      }

      return true;
    };

    // Loop through all days and validate each one
    for (let i = 0; i < daysOfWeek.length; i++) {
      const dayTotal = sumBy(timeSheet, daysOfWeek[i]);
      const isWeekend = daysOfWeek[i] === "sat" || daysOfWeek[i] === "sun";

      if (!validateDayTotal(dayTotal, dayNames[i], isWeekend)) {
        return false;
      }
    }

    return true;
  };

  // const handleAddMore = () => {
  //   if (timeSheet.length >= 20) {
  //     notification.error({
  //       message: "You can't add more than 20 Slots",
  //     });
  //     return;
  //   }
  //   const values = form.getFieldsValue(true);
  //   const actualTimeSheet = generateTimeSheetData(values);
  //   let data = [...actualTimeSheet];
  //   data.push(...InitialTimeSheetDataFn(currentWeek,false,true));
  //   setTimeSheet(data);
  //   form.setFieldsValue(data);
  // };

  const handleAddMore = () => {
    if (timeSheet.length >= 20) {
      notification.error({
        message: "You can't add more than 20 Slots",
      });
      return;
    }
    const values = form.getFieldsValue(true);
    const actualTimeSheet = generateTimeSheetData(values);
    let data = [...actualTimeSheet];
    const newRow = InitialTimeSheetDataFn(currentWeek, false, "draft", true);
    data.push(...newRow);
    setTimeSheet(data);
    form.setFieldsValue(data);
  };

  const handleAddMoreClaim = (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const claimsLength = values[timeSheetIndex]?.claims?.length || 0;

    if (claimsLength >= 5) {
      notification.error({
        message: "You can't add more than 5 claims",
      });
      return;
    }

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        value.claims.push({
          ...initialClaim,
          claimsId: Date.now() + Math.floor(Math.random() * 10000),
        });
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleClearClaim = (timeSheetIndex, claimIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: value.claims
            .map((claim, i) => {
              if (i === claimIndex) {
                return claimIndex === 0 ? initialClaim : null;
              }
              return claim;
            })
            .filter((claim) => claim !== null),
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleResetClaim = (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: [initialClaim],
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handleRemoveClaim = (timeSheetIndex) => {
    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    const timeSheet = data.map((value, index) => {
      if (index === timeSheetIndex) {
        return {
          ...value,
          claims: [],
        };
      }
      return value;
    });

    setTimeSheet(timeSheet);
    form.setFieldsValue(timeSheet);
  };

  const handlePersonInCharge = useCallback((index, value) => {
    setPersonInCharge((prev) => {
      const data = [...prev];
      data[index] = value;
      return data;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearTimesheet = (index, value) => {
    if (index === 0 && Object.keys(value).length === 1) {
      setTimeSheet(initialTimeSheet);
      form.setFieldsValue(initialTimeSheet);
      notification.info({
        message: "Timesheet Section Cleared",
      });
      return;
    }

    const values = form.getFieldsValue(true);

    const actualTimeSheet = generateTimeSheetData(values);

    let data = [...actualTimeSheet];

    if (index >= 0 && index < data.length) {
      data = data.filter((_, i) => i !== index);
      setTimeSheet(data);
      form.setFieldsValue(data);
      notification.info({
        message: "Timesheet Section Removed",
      });
    } else {
      console.error("Invalid index:", index);
    }
  };

   const handleSaveTimesheet = () => {
    const values = form.getFieldsValue(true);
    const originalTimeSheet = timesheetDataRes?.response?.response?.timeSheet;
    const timeSheetData = generateTimeSheetData(
      values,
      true,
      originalTimeSheet
    ).map((entry) => ({
      ...entry,
      status: DRAFT,
      approval: {},
      declined: {},
    }));
    dispatch(setCacheTimesheet(week, timeSheetData));
    onSubmit(values, true, true, originalTimeSheet);
    // notification.success({
    //   message: "Timesheet Saved",
    // });
  };

  const handleSubmitTimesheet = () => {
    const values = form.getFieldsValue(true);
    const originalTimeSheet = timesheetDataRes?.response?.response?.timeSheet;
    const formattedData = generateTimeSheetData(
      values,
      false,
      originalTimeSheet,
      true
    );
    onSubmit(formattedData);
  };

  return {
    timeSheet,
    setTimeSheet,
    initialTimeSheet,
    generateTimeSheetData,
    handleAddMore,
    handleAddMoreClaim,
    handleClearClaim,
    handleResetClaim,
    handleRemoveClaim,
    handlePersonInCharge,
    handleClearTimesheet,
    handleSaveTimesheet,
    isChargeAbleAmtValid,
    handleSubmitTimesheet,
  };
};

export default useTimesheetfns;
